import React, { useEffect, useState } from 'react';
import { useToast } from '../contexts/ToastContext';

const TOAST_COLORS = {
  success: '#28a745',
  error: '#dc3545',
  warning: '#fd7e14',
  info: '#007bff'
};

const TOAST_ICONS = {
  success: '\u2713',   // checkmark
  error: '\u2717',     // cross mark
  warning: '\u26A0',   // warning triangle
  info: '\u2139'       // information symbol
};

const TOAST_LABELS = {
  success: 'Success',
  error: 'Error',
  warning: 'Warning',
  info: 'Info'
};

const styles = {
  container: {
    position: 'fixed',
    top: '20px',
    right: '20px',
    zIndex: 10000,
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    pointerEvents: 'none',
    maxWidth: '400px',
    width: '100%'
  },
  toast: {
    display: 'flex',
    alignItems: 'flex-start',
    padding: '12px 16px',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
    color: '#ffffff',
    fontSize: '14px',
    lineHeight: '1.4',
    pointerEvents: 'auto',
    minWidth: '280px',
    maxWidth: '400px',
    wordBreak: 'break-word',
    transition: 'opacity 0.3s ease, transform 0.3s ease'
  },
  toastEntering: {
    opacity: 1,
    transform: 'translateX(0)'
  },
  toastExiting: {
    opacity: 0,
    transform: 'translateX(100%)'
  },
  toastInitial: {
    opacity: 0,
    transform: 'translateX(100%)'
  },
  iconWrapper: {
    flexShrink: 0,
    marginRight: '10px',
    fontSize: '18px',
    lineHeight: '1',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    backgroundColor: 'rgba(255, 255, 255, 0.2)'
  },
  content: {
    flex: 1,
    marginRight: '8px'
  },
  label: {
    fontWeight: '600',
    marginBottom: '2px',
    fontSize: '13px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    opacity: 0.9
  },
  message: {
    fontSize: '14px',
    lineHeight: '1.4'
  },
  closeButton: {
    flexShrink: 0,
    background: 'none',
    border: 'none',
    color: '#ffffff',
    cursor: 'pointer',
    fontSize: '18px',
    lineHeight: '1',
    padding: '0 0 0 4px',
    opacity: 0.7,
    transition: 'opacity 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px'
  }
};

const ToastItem = ({ toast, onClose }) => {
  const [visible, setVisible] = useState(false);
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    // Trigger enter animation on next frame
    const enterTimer = requestAnimationFrame(() => {
      setVisible(true);
    });
    return () => cancelAnimationFrame(enterTimer);
  }, []);

  const handleClose = () => {
    setExiting(true);
    // Wait for exit animation to finish before removing
    setTimeout(() => {
      onClose(toast.id);
    }, 300);
  };

  const backgroundColor = TOAST_COLORS[toast.type] || TOAST_COLORS.info;
  const icon = TOAST_ICONS[toast.type] || TOAST_ICONS.info;
  const label = TOAST_LABELS[toast.type] || TOAST_LABELS.info;

  let animationStyle;
  if (exiting) {
    animationStyle = styles.toastExiting;
  } else if (visible) {
    animationStyle = styles.toastEntering;
  } else {
    animationStyle = styles.toastInitial;
  }

  return (
    <div
      style={{
        ...styles.toast,
        backgroundColor,
        ...animationStyle
      }}
      role="alert"
      aria-live="assertive"
    >
      <div style={styles.iconWrapper}>
        {icon}
      </div>
      <div style={styles.content}>
        <div style={styles.label}>{label}</div>
        <div style={styles.message}>{toast.message}</div>
      </div>
      <button
        style={styles.closeButton}
        onClick={handleClose}
        onMouseEnter={(e) => { e.target.style.opacity = '1'; }}
        onMouseLeave={(e) => { e.target.style.opacity = '0.7'; }}
        aria-label="Close notification"
        title="Dismiss"
      >
        {'\u2715'}
      </button>
    </div>
  );
};

export const ToastContainer = () => {
  const { toasts, removeToast } = useToast();

  if (toasts.length === 0) {
    return null;
  }

  return (
    <div style={styles.container}>
      {toasts.map((toast) => (
        <ToastItem
          key={toast.id}
          toast={toast}
          onClose={removeToast}
        />
      ))}
    </div>
  );
};
